import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ColDef } from "ag-grid-community";
import moment from "moment";
import { Time04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AgGrid } from "shared/components/AgGrid";
import { formatPhone } from "shared/utils";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Pagination } from "shared/components/Pagination";
import { useWaitlistLeads, WaitlistLead } from "./controller";

const fullName = (lead: WaitlistLead) =>
  [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() ||
  "Unnamed lead";

const contactLine = (lead: WaitlistLead) =>
  lead.primary_phone
    ? formatPhone(lead.primary_phone)
    : (lead.primary_email ?? "");

const joinedAt = (lead: WaitlistLead) =>
  moment(lead.from_waitlist_at ?? lead.created_at).format(
    "MMM D, YYYY [at] h:mm A"
  );

export const WaitlistTab = ({ unitId }: { unitId?: number }) => {
  const navigate = useNavigate();
  const { leads, pageCount, page, setPage, isFetching } =
    useWaitlistLeads(unitId);

  const columns = useMemo<ColDef<WaitlistLead>[]>(
    () => [
      {
        headerName: "#",
        colId: "index",
        width: 57,
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1
      },
      {
        headerName: "Name",
        colId: "name",
        flex: 2,
        cellRenderer: (params: { data?: WaitlistLead }) =>
          params.data ? (
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-content-1000">
                {fullName(params.data)}
              </p>
              <p className="text-xs text-content-400">
                {contactLine(params.data)}
              </p>
            </div>
          ) : null
      },
      {
        headerName: "Source",
        colId: "source",
        flex: 1,
        cellRenderer: (params: { data?: WaitlistLead }) => (
          <span className="text-sm text-content-500">
            {params.data?.source?.name ?? "—"}
          </span>
        )
      },
      {
        headerName: "Added on",
        colId: "added_on",
        flex: 1,
        cellRenderer: (params: { data?: WaitlistLead }) =>
          params.data ? (
            <span className="text-sm text-content-500">
              {joinedAt(params.data)}
            </span>
          ) : null
      }
    ],
    []
  );

  return (
    <div className="mt-4">
      <AgGrid<WaitlistLead>
        columns={columns}
        rows={leads}
        loading={isFetching}
        themeOverrides={{ "--ag-cell-horizontal-padding": "24px" }}
        onCellClicked={(event) => {
          if (event.data) navigate(`/leads/${event.data.id}`);
        }}
        noRowsFallback={
          <NoRowsFallback
            Icon={Time04Icon}
            title="No waitlist yet"
            text="Leads waiting for this unit will appear here"
          />
        }
        footer={
          <Pagination
            className="justify-center md:justify-end"
            total={pageCount}
            current={page}
            onChange={setPage}
          />
        }
        showFooter={pageCount > 1}
      />
    </div>
  );
};
